import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, itemCount } = useCart();
  const { user, axiosInstance } = useAuth();

  const [loading, setLoading] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(user?.address || '');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const deliveryMode = localStorage.getItem('delivery_mode') || 'livraison';
  const freeDelivery = (cart.total || 0) >= 80;
  const deliveryFee = deliveryMode === 'livraison' && !freeDelivery ? 4 : 0;
  const grandTotal = (cart.total || 0) + deliveryFee;

  useEffect(() => {
    if (itemCount === 0) {
      navigate('/cart');
    }
  }, [itemCount, navigate]);

  const handleSubmit = async () => {
    if (deliveryMode === 'livraison' && !deliveryAddress) {
      toast.error('Veuillez renseigner votre adresse de livraison');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === 'livraison' ? deliveryAddress : null,
        special_instructions: specialInstructions || null,
      };

      const response = await axiosInstance.post('/orders', orderData);
      toast.success('Commande confirmée !');
      navigate(`/order-success?order_id=${response.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-4xl mx-auto py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl p-6 shadow-card space-y-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-terracotta/10 rounded-xl flex items-center justify-center">
                  <MapPin className="text-terracotta" size={20} />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-navy">
                    {deliveryMode === 'livraison' ? 'Adresse de livraison' : 'Retrait sur place'}
                  </h2>
                  <p className="text-sm text-navy/60">
                    {deliveryMode === 'livraison'
                      ? freeDelivery
                        ? 'Livraison à Genève — offerte dès 80.–'
                        : 'Livraison à Genève — 4.00 CHF (offerte dès 80.–)'
                      : 'Vous viendrez chercher votre commande — gratuit'}
                  </p>
                </div>
              </div>

              {deliveryMode === 'livraison' ? (
                <textarea
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Entrez votre adresse complète..."
                  rows={3}
                  className="input-field w-full resize-none"
                  data-testid="delivery-address-input"
                />
              ) : (
                <p className="text-sm text-navy/70 bg-sand/60 rounded-xl p-4" data-testid="pickup-info">
                  Nous vous contacterons dès que votre commande sera prête à être retirée.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Instructions spéciales (optionnel)
                </label>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Allergies, code d'entrée, etc."
                  rows={2}
                  className="input-field w-full resize-none"
                  data-testid="special-instructions-input"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading || (deliveryMode === 'livraison' && !deliveryAddress)}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="confirm-order-button"
              >
                {loading ? 'Traitement...' : 'Confirmer la commande'}
              </button>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-card sticky top-28">
              <h3 className="font-heading text-lg font-semibold text-navy mb-4">
                Récapitulatif
              </h3>

              <div className="space-y-3 mb-4">
                {cart.items?.slice(0, 3).map((item) => (
                  <div key={item.dish_id} className="flex justify-between text-sm">
                    <span className="text-navy/70">
                      {item.quantity}x {item.dish?.name}
                    </span>
                    <span className="text-navy font-medium">
                      {item.item_total?.toFixed(2)} CHF
                    </span>
                  </div>
                ))}
                {cart.items?.length > 3 && (
                  <p className="text-sm text-navy/50">
                    + {cart.items.length - 3} autres articles
                  </p>
                )}
              </div>

              <hr className="border-stone-200 my-4" />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-navy/70">Sous-total</span>
                  <span className="text-navy">{(cart.subtotal ?? cart.total)?.toFixed(2)} CHF</span>
                </div>
                {cart.discount > 0 && (
                  <div className="flex justify-between text-sm" data-testid="checkout-formules-discount">
                    <span className="text-sage font-medium">Formules</span>
                    <span className="text-sage font-medium">-{cart.discount.toFixed(2)} CHF</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-navy/70">
                    {deliveryMode === 'livraison' ? 'Livraison' : 'Retrait sur place'}
                  </span>
                  {deliveryMode === 'livraison' ? (
                    deliveryFee > 0 ? (
                      <span className="text-navy">4.00 CHF</span>
                    ) : (
                      <span className="text-sage font-medium">Offerte (dès 80.–)</span>
                    )
                  ) : (
                    <span className="text-sage font-medium">Gratuit</span>
                  )}
                </div>
              </div>

              <hr className="border-stone-200 my-4" />

              <div className="flex justify-between">
                <span className="font-semibold text-navy">Total</span>
                <span className="font-bold text-terracotta text-xl" data-testid="checkout-grand-total">
                  {grandTotal.toFixed(2)} CHF
                </span>
              </div>

              <p className="mt-4 text-xs text-navy/60 bg-sand/60 rounded-xl p-3" data-testid="checkout-payment-note">
                Paiement par terminal (carte ou Twint) lors de la livraison ou du retrait.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
